import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../api/client';
import { resetContent } from './contentSlice';
import { resetBusinessAreas, setSimpleBAs } from './data/businessAreasSlice';
import { resetFolders } from './data/foldersSlice';
import { resetLocalizations, setLocalizations } from './data/localizationSlice';
import { resetNews } from './data/newsSlice';
import { resetObjectLanguages, setObjectLanguages } from './data/objectLanguagesSlice';
import { resetObjects } from './data/objectsSlice';
import { resetProperties } from './data/propertiesSlice';
import { resetUser, setUser } from './data/userSlice';
import { resetSearch } from './searchSlice';
import { version } from '../../../package.json';
import { resetTags } from './data/tagsSlice';
import { resetDoctypes } from './data/doctypesSlice';
import { resetCampaigns } from './data/campaignsSlice';

const initialState = {
	registerStatus: null,
	registerError: null,
	resendActivationKeyStatus: null,
	resendActivationKeyError: null,
	initialLoginDone: false,
	loginStatus: null,
	logoutStatus: null,
	loginError: null,
	feedbackStatus: null,
	feedbackError: null,
};

export const register = createAsyncThunk('auth/register', async (payload) => {
	const params = new URLSearchParams();

	if (payload?.firstName && payload?.lastName && payload?.email && payload?.company) {
		params.append('first_name', payload.firstName);
		params.append('last_name', payload.lastName);
		params.append('mail', payload.email);
		params.append('company', payload.company);
	}
	const response = await client.post(process.env.REACT_APP_API_PATH_REGISTER, params, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }});
	console.log('response', response);
});

export const resendActivationKey = createAsyncThunk('auth/resendActivationKey', async (payload) => {
	const params = new URLSearchParams();

	if (payload?.email) {
		params.append('mail', payload.email);
	}
	const response = await client.post(process.env.REACT_APP_API_PATH_RESEND_ACTIVATION_KEY, params, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }});
	console.log('response', response);
});

export const login = createAsyncThunk('auth/login', async (payload, { dispatch }) => {
	let credentials = null;
	if (payload?.email && payload?.key) {
		credentials = { email: payload.email, key: payload.key, isWeb: 1 };
	}
	const response = await client.post(process.env.REACT_APP_API_PATH_LOGIN, credentials, { method: 'POST' });

	dispatch(setSimpleBAs({ businessAreas: response?.data?.businessareas }));
	dispatch(setLocalizations({ systemLanguages: response?.data?.systemlanguages, translations: response?.data?.translations }));
	dispatch(setUser({ user: response?.data?.user }));
	dispatch(setObjectLanguages({ objectLanguages: response?.data?.objectlanguages }));
});

// TODO: Logout Storage cleanen
export const logout = createAsyncThunk('auth/logout', async (payload, { dispatch }) => {
	const response = await client.post(process.env.REACT_APP_API_PATH_LOGOUT, null, { method: 'POST' });

	if (response) {
		dispatch(resetBusinessAreas());
		dispatch(resetFolders());
		dispatch(resetLocalizations());
		dispatch(resetNews());
		dispatch(resetObjectLanguages());
		dispatch(resetObjects());
		dispatch(resetProperties());
		dispatch(resetTags());
		dispatch(resetDoctypes());
		dispatch(resetCampaigns());
		dispatch(resetUser());
		dispatch(resetSearch());
		dispatch(resetAuth());
		dispatch(resetContent());
	}
});

export const feedback = createAsyncThunk('auth/feedback', async (payload) => {
	const params = new URLSearchParams();
	if (payload?.rating && payload?.message) {
		params.append('rating', payload.rating);
		params.append('message', payload.message);
		params.append('appversion', `web-${version}`);
	}
	const response = await client.post(process.env.REACT_APP_API_PATH_FEEDBACK, params, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }});
	console.log('response', response);
});

export const authSlice = createSlice({
	name: 'app',
	initialState,
	reducers: {
		resetAuth: (state) => {
			state.initialLoginDone = initialState.initialLoginDone;
			state.loginStatus = initialState.loginStatus;
			state.logoutStatus = initialState.logoutStatus;
			state.loginError = initialState.loginError;
			state.registerStatus = initialState.registerStatus;
			state.registerError = initialState.registerError;
			state.resendActivationKeyStatus = initialState.resendActivationKeyStatus;
			state.resendActivationKeyError = initialState.resendActivationKeyError;
			state.feedbackStatus = initialState.feedbackStatus;
			state.feedbackError = initialState.feedbackError;
		},
		setInitialLoginDone: (state) => {
			state.initialLoginDone = true;
		},
		resetFeedback: (state) => {
			state.feedbackStatus = null;
			state.feedbackError = null;
		},
	},
	extraReducers: {
		[register.fulfilled]: (state) => {
			state.registerStatus = 'success';
			state.registerError = null;
		},
		[register.pending]: (state) => {
			state.registerStatus = 'pending';
			state.registerError = null;
		},
		[register.rejected]: (state, action) => {
			state.registerStatus = 'error';
			state.registerError = getErrorTranslationKey(action?.error?.message);
		},
		[resendActivationKey.fulfilled]: (state) => {
			state.resendActivationKeyStatus = 'success';
			state.resendActivationKeyError = null;
		},
		[resendActivationKey.pending]: (state) => {
			state.resendActivationKeyStatus = 'pending';
			state.resendActivationKeyError = null;
		},
		[resendActivationKey.rejected]: (state, action) => {
			state.resendActivationKeyStatus = 'error';
			const errorMessage = action?.error?.message === 'not_found' ? 'forgot_error_not_found' : getErrorTranslationKey(action?.error?.message);
			state.resendActivationKeyError = errorMessage;
		},
		[login.fulfilled]: (state) => {
			state.loginStatus = 'success';
			state.loginError = null;
			state.initialLoginDone = true;
		},
		[login.pending]: (state) => {
			state.loginStatus = 'pending';
			state.loginError = null;
		},
		[login.rejected]: (state, action) => {
			if (state.initialLoginDone) {
				state.loginStatus = 'error';
				state.loginError = getErrorTranslationKey(action?.error?.message);
			} else {
				state.loginStatus = 'initialDone';
				state.loginError = null;
			}
			state.initialLoginDone = true;
		},
		[logout.fulfilled]: (state) => {
			state.logoutStatus = 'success';
		},
		[logout.pending]: (state) => {
			state.logoutStatus = 'pending';
		},
		[logout.rejected]: (state) => {
			state.logoutStatus = 'error';
		},
		[feedback.fulfilled]: (state) => {
			state.feedbackStatus = 'success';
			state.feedbackError = null;
		},
		[feedback.pending]: (state) => {
			state.feedbackStatus = 'pending';
			state.feedbackError = null;
		},
		[feedback.rejected]: (state, action) => {
			state.feedbackStatus = 'error';
			state.feedbackError = getErrorTranslationKey(action?.error?.message);
		},
	},
});

export default authSlice.reducer;
export const { resetAuth, setInitialLoginDone, resetFeedback } = authSlice.actions;


export const selectRegisterStatus = (state) => state.auth.registerStatus;
export const selectRegisterError = (state) => state.auth.registerError;
export const selectResendActivationKeyStatus = (state) => state.auth.resendActivationKeyStatus;
export const selectResendActivationKeyError = (state) => state.auth.resendActivationKeyError;
export const selectLoginStatus = (state) => state.auth.loginStatus;
export const selectLogoutStatus = (state) => state.auth.loginStatus;
export const selectLoginError = (state) => state.auth.loginError;
export const selectInitialLoginDone = (state) => state.auth.initialLoginDone;
export const selectFeedbackStatus = (state) => state.auth.feedbackStatus;
export const selectFeedbackError = (state) => state.auth.feedbackError;

const getErrorTranslationKey = (error) => {
	switch (error) {
	case 'not_active':
		return 'error_not_active';
	case 'not_known':
		return 'error_not_known';
	case 'not_found':
		return 'error_not_known';
	case 'not_authorized':
		return 'error_not_authorized';
	case 'no_credentials':
		return 'error_no_credentials';
	case 'invalid_params':
		return 'error_invalid_params';
	case 'not_initialized':
		return 'error_not_initialized';
	case 'no_key':
		return 'error_no_key';
	case 'text_rest_package_no_user_key':
		return 'error_no_key';
	case 'error_unknown':
		return 'error_unknown';
	case 'already_exists':
		return 'register_error_already_exists';
	case 'failed_to_create_user':
		return 'register_error_already_exists';
	case 'not_possible':
		return 'forgot_error_not_possible';
	case 'failed_send_mail':
		return 'error_invalid_params';
	case 'text_rest_package_no_objects_found':
		return 'multisend_server_error';
	case 'text_rest_package_wrong_object_ids':
		return 'multisend_server_error';
	case 'text_rest_package_no_object_ids':
		return 'multisend_server_error';
	case 'text_rest_package_zip_could_not_created':
		return 'multisend_server_error';
	default:
		return 'error_unknown';
	}
};
